import { type PropsWithChildren, useEffect, useState } from 'react'
import { CssBaseline, ThemeProvider as MuiThemeProvider, ScopedCssBaseline } from '@mui/material'
import { ColorSchemeProvider, useColorScheme } from '../../ColorScheme'
import { useAutoDetectThemeVariant } from '../../hooks/use-auto-detect-theme-variant'
import { UseThemeProvider } from '../ThemeProvider/UseThemeProvider'
import { createTheme } from '../../theme'
import { tenantThemes, type TenantId, type TenantThemeConfig, TenantContextProvider } from '../../tenants'
import { type ThemeOption } from '../../augmentation'

type Props = {
  /**
   * The tenant whose theme should be applied.
   * Falls back to `'dhl'` when the tenant is not found in the registry.
   * @default dhl
   */
  tenant?: TenantId
  /**
   * Optionally override the resolved tenant config at the call-site.
   * Useful for one-off adjustments without adding a new tenant entry.
   */
  overrides?: Partial<TenantThemeConfig>
  /**
   * Use generic css reset, can be useful in shared applications like our locations widget.
   * @default true
   */
  useCssBaseline?: boolean
  /**
   * Specific usecases for our consumers.
   */
  options?: ThemeOption
}

const TenantThemeProviderInner = ({
  tenant = 'dhl',
  overrides,
  useCssBaseline = true,
  options = {},
  children,
}: PropsWithChildren<Props>) => {
  const [activeTenant, setActiveTenant] = useState<TenantId>(tenant)

  // Keep external prop as source-of-truth when caller controls tenant.
  useEffect(() => {
    setActiveTenant(tenant)
  }, [tenant])

  const { activePaletteMode } = useColorScheme()
  const config = tenantThemes[activeTenant] ?? tenantThemes.dhl

  const mergedOptions: ThemeOption = { ...config.options, ...options }
  const variant = useAutoDetectThemeVariant(mergedOptions.variant)

  const tenantPalette = config.palette ? config.palette[activePaletteMode] : undefined

  const baseTheme = createTheme(activePaletteMode, variant, mergedOptions, tenantPalette)

  const themeFn = overrides?.theme ?? config.theme
  const determinedTheme = typeof themeFn === 'function' ? themeFn(baseTheme) : (themeFn ?? baseTheme)

  const inner = useCssBaseline ? (
    <UseThemeProvider theme={determinedTheme}>
      <MuiThemeProvider theme={determinedTheme} noSsr={mergedOptions.noSsr || true}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </UseThemeProvider>
  ) : (
    <UseThemeProvider theme={determinedTheme}>
      <MuiThemeProvider theme={determinedTheme} noSsr={mergedOptions.noSsr || true}>
        <ScopedCssBaseline>{children}</ScopedCssBaseline>
      </MuiThemeProvider>
    </UseThemeProvider>
  )

  return (
    <TenantContextProvider tenant={activeTenant} setTenant={setActiveTenant}>
      {inner}
    </TenantContextProvider>
  )
}

/**
 * Multi-tenant aware theme provider.
 *
 * Resolves the tenant's palette from the registry and builds the full DHL theme
 * (including all component overrides) with that palette — meaning Button colours,
 * Input focus rings, etc. all reflect the tenant brand automatically.
 *
 * All existing features of the base `ThemeProvider` are preserved:
 * dark-mode, device-variant detection, CssBaseline, ColorScheme persistence.
 *
 * @example
 * ```tsx
 * <TenantThemeProvider tenant="ppl">
 *   <App />
 * </TenantThemeProvider>
 * ```
 */
export const TenantThemeProvider = (props: PropsWithChildren<Props>) => (
  <ColorSchemeProvider>
    <TenantThemeProviderInner {...props} />
  </ColorSchemeProvider>
)
